import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, addDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import Chart from 'chart.js/auto'; // Import direct de Chart.js

// Définition de la configuration Firebase et de l'ID de l'application
// Ces variables sont fournies par l'environnement Canvas
const firebaseConfig = {};
const appId = 'default-app-id';
const initialAuthToken = null;

// Initialisation de Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Palette de couleurs "Brilliant Blues" pour une esthétique cohérente
const brilliantBlues = ['#003f5c', '#2f4b7c', '#665191', '#a05195', '#d45087', '#f95d6a', '#ff7c43', '#ffa600'];

// Fonctions utilitaires pour Chart.js
const tooltipTitleCallback = function(tooltipItems) {
    const item = tooltipItems[0];
    let label = item.chart.data.labels[item.dataIndex];
    if (Array.isArray(label)) {
      return label.join(' ');
    } else {
      return label;
    }
};

// Fonctions d'initialisation des graphiques de l'infographie
const initInfographicCharts = (brilliantBlues) => {
    const populationMauritanie = 5271000;
    const internetUsers = 1960000;
    const socialMediaUsers = 1570000;
    const nonInternetUsers = populationMauritanie - internetUsers;
    const internetUsersNotOnSocial = internetUsers - socialMediaUsers;

    const internetPenetrationCtx = document.getElementById('internetPenetrationChart');
    if (internetPenetrationCtx) {
        const existingChart = Chart.getChart(internetPenetrationCtx);
        if (existingChart) existingChart.destroy(); // Détruire le graphique existant s'il y en a un
        new Chart(internetPenetrationCtx.getContext('2d'), {
            type: 'doughnut',
            data: {
                labels: ['Utilisateurs des Médias Sociaux', 'Internautes (hors réseaux sociaux)', 'Non-connectés à Internet'],
                datasets: [{
                    label: 'Population Numérique',
                    data: [socialMediaUsers, internetUsersNotOnSocial, nonInternetUsers],
                    backgroundColor: [brilliantBlues[6], brilliantBlues[7], '#e0e0e0'],
                    borderColor: '#ffffff',
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                    },
                    tooltip: {
                        callbacks: {
                            title: tooltipTitleCallback
                        }
                    }
                }
            }
        });
    }
    
    const digitalGrowthCtx = document.getElementById('digitalGrowthChart');
    if (digitalGrowthCtx) {
        const existingChart = Chart.getChart(digitalGrowthCtx);
        if (existingChart) existingChart.destroy(); // Détruire le graphique existant s'il y en a un
        new Chart(digitalGrowthCtx.getContext('2d'), {
            type: 'bar',
            data: {
                labels: ['2020', '2024'],
                datasets: [{
                    label: 'Apps Banque/Fintech (avec auth. num.)',
                    data: [0, 12],
                    backgroundColor: brilliantBlues[2],
                    borderColor: brilliantBlues[2],
                    borderWidth: 1
                }, {
                    label: 'Services Publics (avec ID num.)',
                    data: [0, 22],
                    backgroundColor: brilliantBlues[4],
                    borderColor: brilliantBlues[4],
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true
                    }
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                    },
                    tooltip: {
                        callbacks: {
                            title: tooltipTitleCallback
                        }
                    }
                }
            }
        });
    }
};

// Fonction de gestion des onglets de l'infographie
const openInfographicTab = (tabName) => {
    let i, tabcontent, tablinks;
    tabcontent = document.querySelectorAll(".tab-content");
    for (i = 0; i < tabcontent.length; i++) {
        tabcontent[i].classList.add("hidden");
    }
    tablinks = document.querySelectorAll(".tab-button");
    for (i = 0; i < tablinks.length; i++) {
        tablinks[i].classList.remove("active");
    }
    document.getElementById(tabName).classList.remove("hidden");
    // Trouver le bouton correspondant et lui ajouter la classe active
    const targetButton = Array.from(tablinks).find(button => button.textContent.toLowerCase().includes(tabName.toLowerCase().replace('pret', 'prêt').replace('capital', 'capital')));
    if (targetButton) {
        targetButton.classList.add("active");
    }
};


// Composant principal de l'application Nour-Finance
function App() {
    // États de l'application
    const [userId, setUserId] = useState(null); // ID de l'utilisateur actuel
    const [projects, setProjects] = useState([]); // Liste des projets de financement participatif
    const [userContributions, setUserContributions] = useState([]); // Contributions de l'utilisateur
    const [newProjectTitle, setNewProjectTitle] = useState(''); // Titre du nouveau projet
    const [newProjectDescription, setNewProjectDescription] = useState(''); // Description du nouveau projet
    const [newProjectTarget, setNewProjectTarget] = useState(''); // Objectif de financement du nouveau projet
    const [loading, setLoading] = useState(true); // Indicateur de chargement initial
    const [error, setError] = useState(null); // Message d'erreur
    const [showModal, setShowModal] = useState(false); // Visibilité du modal de contribution
    const [selectedProject, setSelectedProject] = useState(null); // Projet sélectionné pour la contribution
    const [contributionAmount, setContributionAmount] = useState(''); // Montant de la contribution
    const [modalMessage, setModalMessage] = useState(''); // Message dans le modal (succès/erreur)
    const [currentView, setCurrentView] = useState('accueil'); // Vue actuelle: 'accueil', 'projets', 'dashboard' ou 'infographie'
    const [suggestedRewards, setSuggestedRewards] = useState(null); // Suggestions de récompenses de l'IA
    const [generatingRewards, setGeneratingRewards] = useState(false); // Indicateur de génération de récompenses
    const [projectHighlights, setProjectHighlights] = useState({}); // Stocke les points clés générés par projet
    const [generatingHighlightsFor, setGeneratingHighlightsFor] = useState(null); // ID du projet pour lequel les points clés sont générés

    // Effet pour l'authentification Firebase et la configuration de l'écouteur d'état d'authentification
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Utilisateur connecté
                setUserId(user.uid);
            } else {
                // Aucun utilisateur connecté, tenter de se connecter anonymement ou avec le jeton fourni
                try {
                    if (initialAuthToken) {
                        await signInWithCustomToken(auth, initialAuthToken);
                    } else {
                        await signInAnonymously(auth);
                    }
                } catch (e) {
                    console.error("Erreur d'authentification:", e);
                    setError("Impossible de s'authentifier. Veuillez réessayer.");
                }
            }
            setLoading(false); // Arrêter le chargement une fois l'authentification tentée
        });

        return () => unsubscribe(); // Nettoyage de l'écouteur
    }, []);

    // Effet pour écouter les changements des projets en temps réel depuis Firestore
    useEffect(() => {
        if (!userId) return; // Attendre que l'ID utilisateur soit disponible

        // Chemin de la collection pour les données publiques des projets
        const projectsColRef = collection(db, `artifacts/${appId}/public/data/projects`);
        
        // Écouteur en temps réel pour la collection de projets
        const unsubscribeProjects = onSnapshot(projectsColRef, (snapshot) => {
            const projectsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Trier les projets par date de création (du plus récent au plus ancien)
            projectsData.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
            setProjects(projectsData);
            setLoading(false);
        }, (err) => {
            console.error("Erreur lors de la récupération des projets:", err);
            setError("Impossible de charger les projets. Veuillez réessayer.");
            setLoading(false);
        });

        // Chemin de la collection pour les contributions
        const contributionsColRef = collection(db, `artifacts/${appId}/public/data/contributions`);
        // Requête pour obtenir les contributions de l'utilisateur actuel
        const qContributions = query(contributionsColRef, where("contributorId", "==", userId));

        // Écouteur en temps réel pour les contributions de l'utilisateur
        const unsubscribeContributions = onSnapshot(qContributions, (snapshot) => {
            const contributionsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUserContributions(contributionsData);
        }, (err) => {
            console.error("Erreur lors de la récupération des contributions:", err);
        });

        return () => {
            unsubscribeProjects(); // Nettoyage de l'écouteur des projets
            unsubscribeContributions(); // Nettoyage de l'écouteur des contributions
        };
    }, [userId]); // Dépend de l'ID utilisateur

    // Effet pour initialiser les graphiques de l'infographie lorsque la vue change
    useEffect(() => {
        if (currentView === 'infographie') {
            // S'assurer que le DOM est prêt avant d'initialiser les graphiques
            const timeoutId = setTimeout(() => {
                initInfographicCharts(brilliantBlues);
                // Activer le premier onglet par défaut
                openInfographicTab('don');
            }, 100); // Petit délai pour s'assurer que les éléments canvas sont rendus
            return () => clearTimeout(timeoutId);
        }
    }, [currentView]); // Dépend de la vue actuelle

    // Fonction pour ajouter un nouveau projet
    const handleAddProject = async () => {
        if (!newProjectTitle || !newProjectDescription || !newProjectTarget) {
            setModalMessage("Veuillez remplir tous les champs du projet.");
            setShowModal(true);
            return;
        }
        if (isNaN(newProjectTarget) || parseFloat(newProjectTarget) <= 0) {
            setModalMessage("L'objectif de financement doit être un nombre positif.");
            setShowModal(true);
            return;
        }

        try {
            const projectsColRef = collection(db, `artifacts/${appId}/public/data/projects`);
            await addDoc(projectsColRef, {
                title: newProjectTitle,
                description: newProjectDescription,
                targetAmount: parseFloat(newProjectTarget),
                raisedAmount: 0,
                ownerId: userId,
                createdAt: serverTimestamp() // Horodatage du serveur pour la création
            });
            setNewProjectTitle('');
            setNewProjectDescription('');
            setNewProjectTarget('');
            setSuggestedRewards(null); // Réinitialiser les récompenses suggérées
            setModalMessage("Projet ajouté avec succès !");
            setShowModal(true);
        } catch (e) {
            console.error("Erreur lors de l'ajout du projet:", e);
            setModalMessage("Erreur lors de l'ajout du projet. Veuillez réessayer.");
            setShowModal(true);
        }
    };

    // Fonction pour ouvrir le modal de contribution
    const openContributeModal = (project) => {
        setSelectedProject(project);
        setContributionAmount('');
        setModalMessage('');
        setShowModal(true);
    };

    // Fonction pour gérer la contribution à un projet
    const handleContribute = async () => {
        if (!selectedProject) return;
        if (isNaN(contributionAmount) || parseFloat(contributionAmount) <= 0) {
            setModalMessage("Veuillez entrer un montant de contribution valide.");
            return;
        }

        const amount = parseFloat(contributionAmount);
        const newRaisedAmount = selectedProject.raisedAmount + amount;

        try {
            // Mettre à jour le montant collecté du projet
            const projectDocRef = doc(db, `artifacts/${appId}/public/data/projects`, selectedProject.id);
            await updateDoc(projectDocRef, {
                raisedAmount: newRaisedAmount
            });

            // Enregistrer la contribution dans une nouvelle collection
            const contributionsColRef = collection(db, `artifacts/${appId}/public/data/contributions`);
            await addDoc(contributionsColRef, {
                projectId: selectedProject.id,
                projectTitle: selectedProject.title, // Pour faciliter l'affichage dans le tableau de bord
                contributorId: userId,
                amount: amount,
                timestamp: serverTimestamp()
            });

            setModalMessage(`Contribution de ${amount}€ ajoutée avec succès à "${selectedProject.title}" !`);
            setContributionAmount('');
        } catch (e) {
            console.error("Erreur lors de la contribution:", e);
            setModalMessage("Erreur lors de la contribution. Veuillez réessayer.");
        }
    };

    // Fonction pour générer des suggestions de récompenses via l'API Gemini
    const generateRewardSuggestions = async () => {
        if (!newProjectTitle || !newProjectTarget) {
            setModalMessage("Veuillez entrer un titre de projet et un objectif de financement pour générer des récompenses.");
            setShowModal(true);
            return;
        }
        if (isNaN(newProjectTarget) || parseFloat(newProjectTarget) <= 0) {
            setModalMessage("L'objectif de financement doit être un nombre positif pour générer des récompenses.");
            setShowModal(true);
            return;
        }

        setGeneratingRewards(true);
        setSuggestedRewards(null); // Clear previous suggestions

        try {
            let chatHistory = [];
            const prompt = `En tant qu'expert en financement participatif, proposez 3 niveaux de récompenses pour un projet de crowdfunding intitulé '${newProjectTitle}' avec un objectif de financement de ${newProjectTarget} EUR. Pour chaque niveau, incluez un 'nom', une 'description' et un 'montant_suggere'. Le format de sortie DOIT être un tableau JSON. Les descriptions doivent être concises et attrayantes. Les montants suggérés doivent être réalistes et progressifs.`;
            chatHistory.push({ role: "user", parts: [{ text: prompt }] });
            
            const payload = { 
                contents: chatHistory,
                generationConfig: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: "ARRAY",
                        items: {
                            type: "OBJECT",
                            properties: {
                                "nom": { "type": "STRING" },
                                "description": { "type": "STRING" },
                                "montant_suggere": { "type": "NUMBER" }
                            },
                            "propertyOrdering": ["nom", "description", "montant_suggere"]
                        }
                    }
                }
            };
            const apiKey = ""; // La clé API sera fournie par l'environnement Canvas
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const jsonText = result.candidates[0].content.parts[0].text;
                const parsedRewards = JSON.parse(jsonText);
                setSuggestedRewards(parsedRewards);
            } else {
                setModalMessage("Impossible de générer des suggestions de récompenses. Veuillez réessayer.");
                setShowModal(true);
            }
        } catch (e) {
            console.error("Erreur lors de la génération des récompenses:", e);
            setModalMessage("Une erreur est survenue lors de la génération des récompenses. Veuillez vérifier votre connexion ou réessayer plus tard.");
            setShowModal(true);
        } finally {
            setGeneratingRewards(false);
        }
    };

    // Fonction pour générer les points clés d'un projet via l'API Gemini
    const generateProjectHighlights = async (projectId, projectTitle, projectDescription) => {
        setGeneratingHighlightsFor(projectId);
        setProjectHighlights(prev => ({ ...prev, [projectId]: null })); // Clear previous highlights

        try {
            let chatHistory = [];
            const prompt = `Générez 3-4 points clés concis et accrocheurs pour promouvoir le projet de financement participatif suivant. Le projet s'intitule : '${projectTitle}'. Description : '${projectDescription}'. Formatez la sortie comme une liste à puces (par exemple, '- Point 1\n- Point 2').`;
            chatHistory.push({ role: "user", parts: [{ text: prompt }] });
            
            const payload = { contents: chatHistory };
            const apiKey = ""; // La clé API sera fournie par l'environnement Canvas
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const text = result.candidates[0].content.parts[0].text;
                setProjectHighlights(prev => ({ ...prev, [projectId]: text }));
            } else {
                setProjectHighlights(prev => ({ ...prev, [projectId]: "Impossible de générer les points clés." }));
            }
        } catch (e) {
            console.error("Erreur lors de la génération des points clés:", e);
            setProjectHighlights(prev => ({ ...prev, [projectId]: "Erreur lors de la génération des points clés." }));
        } finally {
            setGeneratingHighlightsFor(null);
        }
    };


    // Affichage des états de chargement et d'erreur
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <p className="text-xl text-gray-700">Chargement de l'application...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-red-100 text-red-800 p-4">
                <p className="text-xl mb-4">Erreur: {error}</p>
                <p>Veuillez rafraîchir la page.</p>
            </div>
        );
    }

    const userCreatedProjects = projects.filter(project => project.ownerId === userId);

    return (
        <div className="min-h-screen bg-gray-100 text-gray-800 p-4 sm:p-6 lg:p-8">
            {/* En-tête de l'application avec navigation */}
            <header className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-col sm:flex-row justify-between items-center">
                <h1 className="text-3xl font-bold text-[#003f5c] mb-2 sm:mb-0">Nour-Finance MVP</h1>
                <nav className="flex space-x-4 mt-2 sm:mt-0">
                    <button
                        onClick={() => setCurrentView('accueil')}
                        className={`py-2 px-4 rounded-md font-semibold transition duration-300 ${currentView === 'accueil' ? 'bg-[#665191] text-white' : 'text-[#665191] hover:bg-gray-100'}`}
                    >
                        Accueil
                    </button>
                    <button
                        onClick={() => setCurrentView('projets')}
                        className={`py-2 px-4 rounded-md font-semibold transition duration-300 ${currentView === 'projets' ? 'bg-[#665191] text-white' : 'text-[#665191] hover:bg-gray-100'}`}
                    >
                        Projets
                    </button>
                    <button
                        onClick={() => setCurrentView('dashboard')}
                        className={`py-2 px-4 rounded-md font-semibold transition duration-300 ${currentView === 'dashboard' ? 'bg-[#665191] text-white' : 'text-[#665191] hover:bg-gray-100'}`}
                    >
                        Mon Tableau de Bord
                    </button>
                </nav>
                {userId && (
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md mt-4 sm:mt-0">
                        Votre ID Utilisateur: <span className="font-mono text-[#665191] break-all">{userId}</span>
                    </div>
                )}
            </header>

            {/* Contenu conditionnel basé sur la vue */}
            {currentView === 'accueil' && (
                <section className="bg-white rounded-lg shadow-md p-8 text-center">
                    <h2 className="text-3xl font-bold text-[#003f5c] mb-4">Bienvenue sur Nour-Finance !</h2>
                    <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
                        Nour-Finance est votre plateforme de financement participatif dédiée à la Mauritanie et à l'Afrique subsaharienne francophone.
                        Nous connectons les porteurs de projets innovants avec des contributeurs passionnés, en tirant parti de la puissance du mobile money.
                        Démocratisez l'accès au financement et catalyser la croissance économique et sociale locale.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={() => setCurrentView('projets')}
                            className="bg-[#ffa600] hover:bg-[#f95d6a] text-white font-bold py-3 px-8 rounded-full text-xl transition duration-300 ease-in-out shadow-lg"
                        >
                            Explorer les Projets
                        </button>
                        <button
                            onClick={() => setCurrentView('infographie')}
                            className="bg-[#a05195] hover:bg-[#d45087] text-white font-bold py-3 px-8 rounded-full text-xl transition duration-300 ease-in-out shadow-lg"
                        >
                            En savoir plus sur l'application
                        </button>
                    </div>
                    <div className="mt-8 text-gray-600">
                        <p className="text-md font-semibold mb-2">Pourquoi Nour-Finance ?</p>
                        <ul className="list-disc list-inside mx-auto max-w-md text-left text-sm">
                            <li>Accès facile au financement pour PME et startups.</li>
                            <li>Soutien aux initiatives sociales et culturelles.</li>
                            <li>Intégration transparente avec les applications bancaires mobiles locales.</li>
                            <li>Transparence et sécurité garanties.</li>
                            <li>Contribution à l'inclusion financière en Afrique.</li>
                        </ul>
                    </div>
                </section>
            )}

            {currentView === 'projets' && (
                <>
                    {/* Section de création de projet */}
                    <section className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-2xl font-semibold text-[#2f4b7c] mb-4">Créer un Nouveau Projet</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <input
                                type="text"
                                placeholder="Titre du projet"
                                className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#665191]"
                                value={newProjectTitle}
                                onChange={(e) => setNewProjectTitle(e.target.value)}
                            />
                            <input
                                type="number"
                                placeholder="Objectif de financement (€)"
                                className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#665191]"
                                value={newProjectTarget}
                                onChange={(e) => setNewProjectTarget(e.target.value)}
                            />
                        </div>
                        <textarea
                            placeholder="Description détaillée du projet..."
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#665191] mb-4"
                            rows="4"
                            value={newProjectDescription}
                            onChange={(e) => setNewProjectDescription(e.target.value)}
                        ></textarea>
                        <button
                            onClick={handleAddProject}
                            className="w-full bg-[#a05195] hover:bg-[#d45087] text-white font-bold py-3 px-6 rounded-md transition duration-300 ease-in-out shadow-md"
                        >
                            Ajouter le Projet
                        </button>

                        {/* Section de génération de récompenses par l'IA */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <h3 className="text-xl font-semibold text-[#2f4b7c] mb-3">Suggestions de Récompenses par l'IA ✨</h3>
                            <button
                                onClick={generateRewardSuggestions}
                                className="w-full bg-[#ff7c43] hover:bg-[#f95d6a] text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-md"
                                disabled={generatingRewards}
                            >
                                {generatingRewards ? 'Génération en cours...' : 'Générer des Suggestions de Récompenses ✨'}
                            </button>
                            {generatingRewards && (
                                <p className="text-center text-sm text-gray-500 mt-2">L'IA prépare vos récompenses...</p>
                            )}
                            {suggestedRewards && (
                                <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-gray-200">
                                    <h4 className="font-bold text-[#003f5c] mb-2">Récompenses suggérées :</h4>
                                    <ul className="list-disc list-inside space-y-2">
                                        {suggestedRewards.map((reward, index) => (
                                            <li key={index} className="text-sm">
                                                <span className="font-semibold">{reward.nom}</span>: {reward.description} (<span className="font-mono">{reward.montant_suggere}€</span>)
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Section des projets existants */}
                    <section className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-2xl font-semibold text-[#2f4b7c] mb-4">Projets Actuels</h2>
                        {projects.length === 0 ? (
                            <p className="text-center text-gray-500">Aucun projet n'a encore été créé. Soyez le premier !</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {projects.map((project) => (
                                    <div key={project.id} className="bg-slate-50 rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold text-[#003f5c] mb-2">{project.title}</h3>
                                            <p className="text-sm text-gray-600 mb-3">{project.description}</p>
                                            <p className="text-sm font-semibold text-[#665191]">
                                                Objectif: {project.targetAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                            </p>
                                            <p className="text-sm font-semibold text-[#ff7c43] mb-3">
                                                Collecté: {project.raisedAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                            </p>
                                            {/* Barre de progression */}
                                            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                                                <div
                                                    className="bg-[#ffa600] h-2.5 rounded-full"
                                                    style={{ width: `${Math.min(100, (project.raisedAmount / project.targetAmount) * 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openContributeModal(project)}
                                            className="mt-4 w-full bg-[#ff7c43] hover:bg-[#f95d6a] text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow"
                                        >
                                            Contribuer
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            {currentView === 'dashboard' && (
                <section className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-2xl font-semibold text-[#2f4b7c] mb-4">Mon Tableau de Bord</h2>

                    {/* Mes Projets Créés */}
                    <div className="mb-8">
                        <h3 className="text-xl font-semibold text-[#003f5c] mb-3">Mes Projets Créés</h3>
                        {userCreatedProjects.length === 0 ? (
                            <p className="text-gray-500">Vous n'avez pas encore créé de projets.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {userCreatedProjects.map((project) => (
                                    <div key={project.id} className="bg-slate-50 rounded-lg shadow-sm p-5 border border-gray-200">
                                        <h4 className="text-lg font-bold text-[#003f5c] mb-1">{project.title}</h4>
                                        <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                                        <p className="text-sm font-semibold text-[#665191]">
                                            Objectif: {project.targetAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                        </p>
                                        <p className="text-sm font-semibold text-[#ff7c43]">
                                            Collecté: {project.raisedAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                        </p>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                            <div
                                                className="bg-[#ffa600] h-2.5 rounded-full"
                                                style={{ width: `${Math.min(100, (project.raisedAmount / project.targetAmount) * 100)}%` }}
                                            ></div>
                                        </div>
                                        {/* Nouveau bouton pour générer les points clés */}
                                        <button
                                            onClick={() => generateProjectHighlights(project.id, project.title, project.description)}
                                            className="mt-4 w-full bg-[#a05195] hover:bg-[#d45087] text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-md text-sm"
                                            disabled={generatingHighlightsFor === project.id}
                                        >
                                            {generatingHighlightsFor === project.id ? 'Génération...' : 'Générer Points Clés ✨'}
                                        </button>
                                        {projectHighlights[project.id] && (
                                            <div className="mt-3 p-3 bg-gray-100 rounded-md text-sm whitespace-pre-wrap">
                                                <h5 className="font-semibold text-[#003f5c] mb-1">Points Clés :</h5>
                                                {projectHighlights[project.id]}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mes Contributions */}
                    <div>
                        <h3 className="text-xl font-semibold text-[#003f5c] mb-3">Mes Contributions</h3>
                        {userContributions.length === 0 ? (
                            <p className="text-gray-500">Vous n'avez pas encore contribué à des projets.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {userContributions.map((contribution) => (
                                    <div key={contribution.id} className="bg-slate-50 rounded-lg shadow-sm p-5 border border-gray-200">
                                        <h4 className="text-lg font-bold text-[#2f4b7c] mb-1">
                                            Contribution à: {contribution.projectTitle || 'Projet Inconnu'}
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                            Montant: {contribution.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Le: {contribution.timestamp?.toDate().toLocaleDateString('fr-FR') || 'N/A'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {currentView === 'infographie' && (
                <div className="container mx-auto p-4 md:p-8 max-w-7xl">
                    <style>
                        {`
                        .stat-card h3 {
                            font-size: 4rem;
                            font-weight: 900;
                            line-height: 1;
                            color: #ff7c43;
                        }
                        .chart-container {
                            position: relative;
                            width: 100%;
                            max-width: 600px;
                            margin-left: auto;
                            margin-right: auto;
                            height: 300px;
                            max-height: 400px;
                        }
                        @media (min-width: 768px) {
                            .chart-container {
                                height: 350px;
                            }
                        }
                        .flow-step {
                            display: flex;
                            align-items: center;
                            text-align: center;
                            flex-direction: column;
                        }
                        .flow-arrow {
                            font-size: 2rem;
                            color: #d1d5db;
                            margin: 0.5rem 0;
                            transform: rotate(90deg);
                        }
                        @media (min-width: 1024px) {
                           .flow-arrow {
                                transform: rotate(0deg);
                                margin: 0 1rem;
                            }
                        }
                        .tab-button {
                            background-color: #e5e7eb; /* bg-gray-200 */
                            color: #4b5563; /* text-gray-700 */
                            border: 1px solid #d1d5db; /* border-gray-300 */
                            border-bottom: none; /* Remove bottom border to make it touch content */
                            padding: 0.75rem 1.5rem;
                            border-top-left-radius: 0.5rem; /* rounded-t-lg */
                            border-top-right-radius: 0.5rem; /* rounded-t-lg */
                            box-shadow: 0 -2px 5px -2px rgba(0, 0, 0, 0.05); /* subtle top shadow */
                            transition: all 300ms ease-in-out;
                            margin-bottom: -1px; /* Overlap with content border */
                            z-index: 10; /* Bring active tab to front */
                            position: relative;
                        }
                        .tab-button:hover {
                            background-color: #d1d5db; /* hover:bg-gray-300 */
                        }
                        .tab-button.active {
                            background-color: #fff; /* bg-white */
                            color: #003f5c; /* text-[#003f5c] */
                            border-color: #665191; /* Active border color */
                            border-bottom-color: #fff; /* Hide bottom border for active tab */
                            font-weight: 600; /* semibold */
                            box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15); /* stronger top shadow for active */
                        }
                        .tab-content {
                            background-color: #fff; /* bg-white */
                            border: 1px solid #d1d5db; /* border-gray-300 */
                            border-top-right-radius: 0.5rem; /* rounded-tr-lg */
                            border-bottom-left-radius: 0.5rem; /* rounded-bl-lg */
                            border-bottom-right-radius: 0.5rem; /* rounded-br-lg */
                            padding: 1.5rem;
                            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); /* shadow-md */
                            margin-top: -1px; /* Overlap with tab button border */
                        }
                        `}
                    </style>
                    <main className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <section className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6 mb-6">
                            <h2 className="text-3xl font-bold text-[#003f5c] mb-4 text-center">Une Opportunité À Exploiter</h2>
                            <p className="text-center max-w-3xl mx-auto mb-8">
                                La Mauritanie est à un carrefour numérique. Une pénétration mobile massive coexiste avec un secteur bancaire traditionnel qui laisse de côté la majorité de la population et des entreprises. Cet écart représente un terrain fertile pour une solution de financement participatif innovante, conçue pour le mobile et culturellement pertinente.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div className="stat-card bg-slate-50 p-6 rounded-lg">
                                    <h3 className="text-[#ff7c43]">106,1%</h3>
                                    <p className="font-semibold text-[#003f5c]">Taux de Pénétration Mobile</p>
                                    <p className="text-sm text-gray-500">Un accès quasi universel au mobile, prêt pour les services financiers.</p>
                                </div>
                                <div className="stat-card bg-slate-50 p-6 rounded-lg">
                                    <h3 className="text-[#f95d6a]">~18%</h3>
                                    <p className="font-semibold text-[#003f5c]">Taux de Bancarisation</p>
                                    <p className="text-sm text-gray-500">Une immense population sous-bancarisée a besoin d'alternatives.</p>
                                </div>
                                <div className="stat-card bg-slate-50 p-6 rounded-lg">
                                    <h3 className="text-[#ffa600]">70%</h3>
                                    <p className="font-semibold text-[#003f5c]">Besoin de Financement PME non couvert</p>
                                    <p className="text-sm text-gray-500">Taux moyen des besoins de financement à long terme non couverts en Afrique.</p>
                                </div>
                            </div>
                        </section>
                        
                        <section className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-bold text-[#003f5c] mb-4 text-center">Le Paysage Numérique Mauritanien</h2>
                            <p className="text-center text-sm mb-4">La pénétration d'Internet est en croissance, et une large part des internautes est déjà active sur les réseaux sociaux. C'est un public connecté, réceptif aux plateformes numériques.</p>
                            <div className="chart-container">
                                <canvas id="internetPenetrationChart"></canvas>
                            </div>
                            <p className="text-xs text-center mt-2 text-gray-500">Visualisation de la population totale de la Mauritanie, montrant la part des utilisateurs d'Internet et la part de ces derniers qui utilisent les médias sociaux.</p>
                        </section>

                        <section className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-bold text-[#003f5c] mb-4 text-center">La Transformation Digitale Accélère</h2>
                            <p className="text-center text-sm mb-4">L'écosystème numérique mauritanien a connu une croissance explosive, prouvant une adoption rapide des services digitaux par le gouvernement et les institutions financières.</p>
                            <div className="chart-container">
                                <canvas id="digitalGrowthChart"></canvas>
                            </div>
                            <p className="text-xs text-center mt-2 text-gray-500">Comparaison du nombre de services digitaux disponibles entre 2020 et 2024.</p>
                        </section>
                        
                        <section className="lg:col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6 flex flex-col justify-center items-center">
                            <h2 className="text-2xl font-bold text-[#003f5c] mb-4 text-center">Pont entre Tradition et Technologie</h2>
                            <p className="text-center text-sm">Nour-Finance s'inspire des pratiques de financement collectif informelles et profondément ancrées en Mauritanie (Tontines, Piyés) pour les formaliser dans un cadre numérique, sécurisé et transparent.</p>
                            <div className="text-6xl my-6 text-[#ffa600]">🤝</div>
                            <p className="text-center font-semibold text-[#a05195]">Mobile + USSD = Inclusion Totale</p>
                            <p className="text-center text-sm mt-2">Notre approche hybride garantit l'accès à tous, que ce soit via notre application smartphone ou via de simples codes USSD sur n'importe quel téléphone mobile.</p>
                        </section>
                        
                        <section className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-3xl font-bold text-[#003f5c] mb-4 text-center">Des Modèles de Financement Flexibles</h2>
                            <p className="text-center max-w-3xl mx-auto mb-8">Pour répondre aux divers besoins des porteurs de projets et des contributeurs, Nour-Finance proposera plusieurs types de campagnes de financement participatif, introduits progressivement.</p>
                            
                            <div className="flex flex-wrap justify-center mb-0 space-x-2 md:space-x-4">
                                <button className="tab-button active" onClick={() => openInfographicTab('don')}>Don</button>
                                <button className="tab-button" onClick={() => openInfographicTab('recompense')}>Récompense</button>
                                <button className="tab-button" onClick={() => openInfographicTab('pret')}>Prêt (P2P)</button>
                                <button className="tab-button" onClick={() => openInfographicTab('capital')}>Capital (Equity)</button>
                            </div>

                            <div id="don" className="tab-content">
                                <h3 className="font-bold text-lg text-[#003f5c] mb-2">Don</h3>
                                <p className="text-sm">🎁 Idéal pour les projets sociaux, culturels et personnels, ce modèle reflète les concepts de "Piyé" et "Lawha" traditionnels. Les contributeurs donnent des fonds sans attendre de retour financier direct, mais par solidarité ou pour soutenir une cause. Ce modèle est simple à mettre en œuvre et encourage une large participation, comme on le voit avec des plateformes telles que M-Changa et Orange Collecte.</p>
                            </div>
                            <div id="recompense" className="tab-content hidden">
                                <h3 className="font-bold text-lg text-[#003f5c] mb-2">Récompense</h3>
                                <p className="text-sm">🏆 Adapté aux projets créatifs, aux préventes de produits ou aux initiatives de petites entreprises. Les contributeurs reçoivent une récompense non financière en échange de leur soutien, comme un produit en avant-première, une reconnaissance spéciale ou une expérience unique. C'est un point d'entrée courant pour les plateformes de financement participatif dans le monde.</p>
                            </div>
                            <div id="pret" className="tab-content hidden">
                                <h3 className="font-bold text-lg text-[#003f5c] mb-2">Prêt (P2P)</h3>
                                <p className="text-sm">💰 Facilite les prêts directs entre individus ou aux entreprises. Il peut formaliser certains aspects des tontines traditionnelles et combler le déficit de crédit pour les PME. Les contributeurs prêtent de l'argent avec l'attente d'un remboursement, souvent avec des intérêts. Ce modèle nécessite une surveillance réglementaire plus stricte en raison de sa nature financière.</p>
                            </div>
                            <div id="capital" className="tab-content hidden">
                                <h3 className="font-bold text-lg text-[#003f5c] mb-2">Capital (Equity)</h3>
                                <p className="text-sm">📈 Permet aux startups et PME de lever des capitaux en vendant de petites parts de leur entreprise à un grand nombre d'investisseurs. Les contributeurs deviennent actionnaires et peuvent bénéficier de la croissance future de l'entreprise. C'est le modèle le plus complexe sur le plan réglementaire, mais il offre un potentiel de croissance significatif pour les entreprises innovantes.</p>
                            </div>
                        </section>

                        <section className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-3xl font-bold text-[#003f5c] mb-6 text-center">Comment Ça Marche ? Un Processus Simple et Sécurisé</h2>
                            <div className="flex flex-col lg:flex-row justify-around items-center space-y-4 lg:space-y-0 lg:space-x-4">
                                <div className="flow-step">
                                    <div className="text-4xl p-4 bg-[#ffa600] text-white rounded-full h-16 w-16 flex items-center justify-center">1</div>
                                    <h3 className="font-bold mt-2">Création du Projet</h3>
                                    <p className="text-sm max-w-xs">Le porteur de projet soumet sa campagne avec un plan détaillé et des objectifs clairs.</p>
                                </div>
                                <div className="flow-arrow hidden lg:block">→</div>
                                <div className="flow-arrow block lg:hidden">↓</div>
                                <div className="flow-step">
                                    <div className="text-4xl p-4 bg-[#f95d6a] text-white rounded-full h-16 w-16 flex items-center justify-center">2</div>
                                    <h3 className="font-bold mt-2">Validation & KYC</h3>
                                    <p className="text-sm max-w-xs">Notre équipe vérifie le projet et l'identité du porteur (KYC) pour garantir la sécurité.</p>
                                </div>
                                <div className="flow-arrow hidden lg:block">→</div>
                                <div className="flow-arrow block lg:hidden">↓</div>
                                <div className="flow-step">
                                    <div className="text-4xl p-4 bg-[#d45087] text-white rounded-full h-16 w-16 flex items-center justify-center">3</div>
                                    <h3 className="font-bold mt-2">Contribution</h3>
                                    <p className="text-sm max-w-xs">Les contributeurs financent les projets via Mobile Money (Bankily, Masrvi) ou USSD.</p>
                                </div>
                                <div className="flow-arrow hidden lg:block">→</div>
                                <div className="flow-arrow block lg:hidden">↓</div>
                                <div className="flow-step">
                                    <div className="text-4xl p-4 bg-[#a05195] text-white rounded-full h-16 w-16 flex items-center justify-center">4</div>
                                    <h3 className="font-bold mt-2">Suivi & Réussite</h3>
                                    <p className="text-sm max-w-xs">Les fonds sont débloqués. Le projet démarre avec un suivi transparent pour les contributeurs.</p>
                                </div>
                            </div>
                        </section>
                        
                        <section className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-3xl font-bold text-[#003f5c] mb-6 text-center">Un Modèle Économique Durable et Diversifié</h2>
                            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <div className="flex items-start space-x-4">
                                    <span className="text-3xl mt-1">📈</span>
                                    <div>
                                        <h3 className="font-bold text-lg text-[#2f4b7c]">Frais de Transaction</h3>
                                        <p className="text-sm">Une commission de 3-5% sur les fonds levés avec succès. Notre succès est lié à celui des porteurs de projets.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <span className="text-3xl mt-1">📱</span>
                                    <div>
                                        <h3 className="font-bold text-lg text-[#2f4b7c]">Frais de Service (Paiements)</h3>
                                        <p className="text-sm">Frais minimes sur les transferts d'argent et les paiements de factures via la plateforme.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <span className="text-3xl mt-1">⭐</span>
                                    <div>
                                        <h3 className="font-bold text-lg text-[#2f4b7c]">Services à Valeur Ajoutée</h3>
                                        <p className="text-sm">Offres premium payantes : coaching, aide au business plan, promotion de campagnes.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <span className="text-3xl mt-1">🤝</span>
                                    <div>
                                        <h3 className="font-bold text-lg text-[#2f4b7c]">Partenariats Stratégiques</h3>
                                        <p className="text-sm">Partage de revenus avec les banques, les opérateurs télécoms, et subventions d'organisations de développement.</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-3xl font-bold text-[#003f5c] mb-6 text-center">Feuille de Route : De la Mauritanie à l'Afrique Francophone</h2>
                            <p className="text-center max-w-3xl mx-auto mb-8">Nous adoptons une approche de croissance progressive, en consolidant notre modèle en Mauritanie avant une expansion régionale ciblée.</p>
                            <div className="relative w-full">
                                <div className="absolute left-1/2 h-full w-0.5 bg-gray-300"></div>
                                <div className="relative flex flex-col items-center">
                                    
                                    <div className="mb-8 w-full flex justify-start">
                                        <div className="w-5/12"></div>
                                        <div className="w-2/12 flex justify-center">
                                            <div className="z-10 bg-[#ff7c43] text-white rounded-full h-12 w-12 flex items-center justify-center font-bold">1</div>
                                        </div>
                                        <div className="w-5/12 bg-slate-50 p-4 rounded-lg shadow">
                                            <h3 className="font-bold text-[#003f5c]">Phase 1 : Lancement en Mauritanie</h3>
                                            <p className="text-sm">Projets pilotes, partenariats avec Bankily/Masrvi, engagement communautaire et utilisation du Sandbox réglementaire de la BCM.</p>
                                        </div>
                                    </div>

                                    <div className="mb-8 w-full flex justify-end">
                                        <div className="w-5/12 bg-slate-50 p-4 rounded-lg shadow text-right">
                                            <h3 className="font-bold text-[#003f5c]">Phase 2 : Expansion UEMOA</h3>
                                            <p className="text-sm">Ciblage de marchés clés (Sénégal, Côte d'Ivoire) avec des écosystèmes Mobile Money matures. Adaptation locale de la plateforme.</p>
                                        </div>
                                        <div className="w-2/12 flex justify-center">
                                            <div className="z-10 bg-[#d45087] text-white rounded-full h-12 w-12 flex items-center justify-center font-bold">2</div>
                                        </div>
                                        <div className="w-5/12"></div>
                                    </div>

                                    <div className="w-full flex justify-start">
                                        <div className="w-5/12"></div>
                                        <div className="w-2/12 flex justify-center">
                                            <div className="z-10 bg-[#665191] text-white rounded-full h-12 w-12 flex items-center justify-center font-bold">3</div>
                                        </div>
                                        <div className="w-5/12 bg-slate-50 p-4 rounded-lg shadow">
                                            <h3 className="font-bold text-[#003f5c]">Phase 3 : Leader Régional</h3>
                                            <p className="text-sm">Devenir la plateforme de financement participatif de référence en Afrique de l'Ouest francophone, en intégrant plus de services et de pays.</p>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            )}

            {/* Modal de contribution */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h3 className="text-2xl font-bold text-[#003f5c] mb-4">
                            {selectedProject ? `Contribuer à "${selectedProject.title}"` : 'Message'}
                        </h3>
                        {selectedProject && (
                            <>
                                <p className="text-gray-700 mb-4">
                                    Objectif: {selectedProject.targetAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} <br />
                                    Collecté: {selectedProject.raisedAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                </p>
                                <input
                                    type="number"
                                    placeholder="Montant de la contribution (€)"
                                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#665191] mb-4"
                                    value={contributionAmount}
                                    onChange={(e) => setContributionAmount(e.target.value)}
                                />
                                <button
                                    onClick={handleContribute}
                                    className="w-full bg-[#ff7c43] hover:bg-[#f95d6a] text-white font-bold py-3 px-6 rounded-md transition duration-300 ease-in-out shadow-md mb-4"
                                >
                                    Confirmer la Contribution
                                </button>
                            </>
                        )}
                        {modalMessage && (
                            <p className="text-center text-sm text-gray-700 mt-2">{modalMessage}</p>
                        )}
                        <button
                            onClick={() => setShowModal(false)}
                            className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
